import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { LinkIcon } from 'lucide-react';
import { DetailBox } from '../DetailBox/DetailBox';
import { getWidgetStores } from '@/entities/widget';
import type { DetailSectionItemProps, SourceInfo } from './DetailSectionItem.types';
import { useTranslation } from 'react-i18next';

export function DetailSectionItem({ icon, title, description, sources }: DetailSectionItemProps) {
  // ChatMarkdownRenderer via the entities/widget binding seam.
  const { ChatMarkdownRenderer } = getWidgetStores().chatStore;
  return (
    <DetailBox variant="outlined">
      <div className="flex flex-1 items-start gap-4">
        {icon && <span className="flex items-center text-muted-foreground">{icon}</span>}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <span className="text-body-lg font-medium text-muted-foreground">{title}</span>
            <div className="text-body-lg text-foreground">
              <ChatMarkdownRenderer content={description} disableAnimation inline />
            </div>
          </div>
          {sources && (Array.isArray(sources) ? sources.length > 0 : true) && (
            <Sources sources={sources} />
          )}
        </div>
      </div>
    </DetailBox>
  );
}

function Sources({ sources }: { sources: SourceInfo | SourceInfo[] }) {
  const { t } = useTranslation('contentTest');
  const sourcesArray = Array.isArray(sources) ? sources : [sources];
  const buttonText = t('socialBriefWidget.detailSectionItem.sourceCount', {
    count: sourcesArray.length,
  });

  const tooltipContent = (
    <div className="flex flex-col gap-1">
      {sourcesArray.map((source, index) => (
        <div key={index}>
          {source.url ? (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              onClick={(e) => e.stopPropagation()}
            >
              {source.title}
            </a>
          ) : (
            <span>{source.title}</span>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="w-fit">
            <Button variant="outline">
              <LinkIcon />
              {buttonText}
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltipContent}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
